import React, { useContext, useState } from 'react';
import moment from 'moment';
import { translate } from '../../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { TaskExportConfigurationContext } from '../../../../../../context/TaskExportConfigurationContext';
import { TaskExportConfigurationRequest } from '../../../../../../requests/taskExportConfiguration.request';
import { useRequest } from '../../../../../../requests/common/requestHook';
import { ExportFile } from '../../../../../../types/export/export';
import { ExportUtil } from '../../../../../../util/export.util';
import { Routing } from '../../../../../../util/routes.util';
import { Button, CancelButton } from '../../../../../components/Button';
import { ActionModal } from '../../../../../components/Modal';
import { ExportItems } from '../../../../../components/ExportItems';

interface TaskExportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  id: string;
}

export function TaskExportModal({ open, setOpen, id }: TaskExportModalProps) {
  const { taskExportConfigurations } = useContext(TaskExportConfigurationContext);

  const [exportTasks, { loading }] = useRequest(TaskExportConfigurationRequest.download);

  const [selected, setSelected] = useState<Set<string>>(
    new Set([...taskExportConfigurations.values()].filter(config => config.default).map(config => config.id)),
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleDownload = async () => {
    if (!selected.size) {
      const file = await exportTasks({
        taskId: id,
        timezone: moment.tz.guess(),
        locale: (window.navigator as any)['userLanguage'] || window.navigator.language,
      });
      if (!file) return;
      ExportUtil.downloadExport(file);
    } else {
      const files: ExportFile[] = [];
      for (const configId of Array.from(selected)) {
        const file = await exportTasks({
          taskId: id,
          configId,
          timezone: moment.tz.guess(),
          locale: (window.navigator as any)['userLanguage'] || window.navigator.language,
        });
        if (file) files.push(file);
      }
      if (!files.length) return;

      if (files.length === 1) {
        ExportUtil.downloadExport(files[0]);
      } else {
        ExportUtil.downloadZip(files, taskExportConfigurations);
      }
    }

    setSelected(new Set());
    handleClose();
  };

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={handleClose} disabled={loading} />
      <Button loading={loading} text={translate({ key: TranslationKey.export })} onClick={handleDownload} />
    </div>
  );

  return (
    <ActionModal
      footer={footer}
      title={`${translate({ key: TranslationKey.export })} ${translate({ key: TranslationKey.task, count: 'plural' })}`}
      open={open}
      setOpen={setOpen}
      className='max-w-[720px]'
    >
      <ExportItems
        items={[...taskExportConfigurations.values()]}
        selected={selected}
        setSelected={setSelected}
        createPath={Routing.tasks.settings.report.create()}
      />
    </ActionModal>
  );
}
